/**
 * @file CreateHaulageJob.js
 * @version 2.3.2
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-07-29
 * @modified 2026-10-08
 * @description Create a Haulage Job (HO-). Supplier is chosen from the "Haulage
 *   Job Suppliers" contact group; the delivery target is one of that supplier's
 *   assigned customers. No PO is created.
 *
 *   THE MANUAL ADDRESS IS GONE (2.0.0). A delivery target used to be either an
 *   assigned customer or an address typed into a nine-field form on the right of
 *   the screen. That form produced a job whose customer existed nowhere else —
 *   no contact record, so no notes, no vehicle access, nothing to look up next
 *   time, and a second delivery to the same farm typed from scratch. Creating
 *   the contact properly takes the same keystrokes and leaves something behind,
 *   so "New Contact" replaces it: the contact is made, assigned to this
 *   supplier, and selected, all without leaving the job.
 *
 *   The API still accepts manual_entry and still reads it where it exists — this
 *   screen simply never sends one.
 */

import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';
import './CreateCustomerOrder.css';
import './CreateHaulageJob.css';
import SearchableDropdown from './SearchableDropdown';
import Dropdown from './Dropdown';
import DateInput from './DateInput';
import Input from './Input';
import Textarea from './Textarea';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import ContactModal from './ContactModal';
import LoadingSpinner from './LoadingSpinner';
import ContactEditModal from './ContactEditModal';
import { useSlideOutButton } from './SlideOutButton';
import CustomerNotesPanel from './CustomerNotesPanel';
import { withStoreTag } from '../utils/stockSupplier';
import { formatDecimalInput } from '../utils/numberFormatters';

/*
 * Where a menu is allowed to open.
 *
 * Without a boundary the positioning hook measures against the nearest SCROLL
 * ancestor, and on this screen that is whatever short box the control happens
 * to sit in — the job-details grid carries overflow-x and is only as tall as
 * its one row, so a menu opened there decides there is no room either way and
 * clamps to its 120px floor. Measured against the page area instead it gets the
 * real distance to the bottom, and escapeOverflow lets it use it. Same pair, and
 * the same reason, as MENU_ESCAPE in OrderLinesGrid.
 */
const MENU_ESCAPE = {
  escapeOverflow: true,
  reflowOnScroll: true,
  boundarySelector: '.content-inner',
};

/* Tonnes first: bulk is the commonest haulage job by a distance, and it had
   no option at all — people were picking a bag size and correcting the
   quantity, or leaving units blank. Capital T per the project rule. */
/* The rate picker's sentinel for "not off the card". Not a postcode prefix and
   not 'default', so it can never collide with a real band. */
const MANUAL_RATE = '__manual__';

/* Mirrors DELIVERY_STATUSES in SearchHaulageJobs and the values the PATCH
   route accepts. Only offered when editing. */
const DELIVERY_STATUSES = ['Pending', 'Delivered', 'Cancelled'];

const UNIT_OPTIONS = [
  { value: 'Tonnes', label: 'Tonnes (T)' },
  { value: 'Bags (20kg)', label: 'Bags (20kg)' },
  { value: 'Bags (25kg)', label: 'Bags (25kg)' },
  { value: 'Totes (0.5T)', label: 'Totes (0.5T)' }
];

function CreateHaulageJob({ user, onNavigate }) {
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [supplierCustomers, setSupplierCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  // Making a contact, and looking one up, without leaving a half-built job.
  const [showNewContact, setShowNewContact] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null });

  const [deliveryDate, setDeliveryDate] = useState('');
  const [productDetails, setProductDetails] = useState('');
  const [quantity, setQuantity] = useState('');
  const [units, setUnits] = useState('');
  const [deliveryInstructions, setDeliveryInstructions] = useState('');
  // Optional Moffett (offload) charge — off by default; when on, adds £5.00
  // (editable) to the job as its own invoice line.
  const [moffett, setMoffett] = useState(false);
  const [moffettCharge, setMoffettCharge] = useState('5.00');

  // Rate card for the selected supplier: default fallback + postcode-prefix rates.
  const [rateCard, setRateCard] = useState({ default_rate: null, rates: [] });
  // The chosen rate: 'default' or a postcode prefix. Auto-suggested from the
  // delivery postcode; the user can override.
  const [selectedRateKey, setSelectedRateKey] = useState('default');
  /*
   * A typed-in rate, for the job the card does not cover: a one-off run, a
   * price agreed on the phone, a backhaul. The card stays the default — it is
   * right nearly every time — but a rate picker with no way to say "not this
   * time" meant editing the supplier's card to price one job, which then
   * mis-priced the next.
   */
  const [manualRate, setManualRate] = useState('');

  const [saving, setSaving] = useState(false);
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'error' });

  /*
   * Editing happens HERE, not in a form of its own.
   *
   * Every other entity in the system works this way — Create Bill, Create
   * Purchase Order, Create Invoice and Create Customer Order all load an
   * existing record and PUT it — and haulage jobs were the one exception,
   * with a cut-down modal on the search screen that offered seven of the
   * job's fields and quietly lacked the rate picker and the delivery
   * customer. One form means the two cannot drift.
   *
   * Delivery status is the one field that only exists here in edit mode: a
   * job being raised has not been delivered.
   */
  /*
   * The loaded job's customer and rate cannot be applied straight away: both
   * depend on a list that is fetched off the back of the supplier, and the
   * effects that fetch them also clear the selection (choosing a new supplier
   * SHOULD reset the customer). So the loaded values wait here and are applied
   * once their list has arrived.
   */
  const [pendingCustomerId, setPendingCustomerId] = useState(null);
  const [pendingRate, setPendingRate] = useState(null);
  const [editingJobId, setEditingJobId] = useState(null);
  const [editJobNumber, setEditJobNumber] = useState('');
  const [deliveryStatus, setDeliveryStatus] = useState('Pending');
  const [loadingJob, setLoadingJob] = useState(false);
  const editMode = !!editingJobId;

  useSlideOutButton([saving, selectedSupplierId, selectedCustomerId, editingJobId]);

  /*
   * Arriving from the search screen's Edit action, which leaves the id here
   * and navigates — the same handoff Create Bill and the rest use.
   *
   * Read and cleared on mount so a later re-entry to this screen is a fresh
   * create rather than the same job again.
   */
  useEffect(() => {
    const id = typeof window !== 'undefined' && sessionStorage.getItem('editHaulageJobId');
    if (!id) return;
    sessionStorage.removeItem('editHaulageJobId');
    setLoadingJob(true);
    fetch(`${API_URL}/api/haulage-jobs/${id}`, { credentials: 'include' })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Could not load the haulage job.');
        return data;
      })
      .then((job) => {
        setEditingJobId(job.id);
        setEditJobNumber(job.job_number || '');
        setSelectedSupplierId(job.supplier_account_id ? String(job.supplier_account_id) : '');
        setPendingCustomerId(job.customer_account_id ? String(job.customer_account_id) : '');
        setDeliveryDate(job.delivery_date ? String(job.delivery_date).slice(0, 10) : '');
        setProductDetails(job.product_details || '');
        setQuantity(job.quantity == null ? '' : formatDecimalInput(job.quantity));
        setUnits(job.units || '');
        setDeliveryInstructions(job.delivery_instructions || '');
        setDeliveryStatus(job.delivery_status || 'Pending');
        const charge = Number(job.moffett_charge);
        setMoffett(charge > 0);
        if (charge > 0) setMoffettCharge(String(job.moffett_charge));
        /*
         * An overridden rate has no prefix of its own, so it cannot be told
         * from a default one by the prefix alone — it is told by the money.
         * Where the stored rate is not what the card would give for that band,
         * it was typed, and it has to come back as typed or opening the job
         * would silently re-price it off the card.
         */
        setPendingRate({
          key: job.rate_postcode_prefix || 'default',
          rate: job.rate == null ? null : Number(job.rate)
        });
      })
      .catch((e) => setAlertDialog({ isOpen: true, message: e.message, type: 'error' }))
      .finally(() => setLoadingJob(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/api/contacts/haulage-suppliers`, { credentials: 'include' })
      .then(r => r.json())
      .then(setSuppliers)
      .catch(err => console.error('Failed to load haulage suppliers:', err));
  }, []);

  // Load the selected supplier's assigned customers for the contact dropdown.
  useEffect(() => {
    if (!selectedSupplierId) { setSupplierCustomers([]); return; }
    fetch(`${API_URL}/api/contacts/haulage-suppliers/${selectedSupplierId}/customers`, { credentials: 'include' })
      .then(r => r.json())
      .then(setSupplierCustomers)
      .catch(err => console.error('Failed to load supplier customers:', err));
    setSelectedCustomerId('');
  }, [selectedSupplierId]);

  // Apply the loaded job's customer once its list is in, then forget it — so a
  // later supplier change clears the customer as it should.
  useEffect(() => {
    if (!pendingCustomerId || supplierCustomers.length === 0) return;
    setSelectedCustomerId(pendingCustomerId);
    setPendingCustomerId(null);
  }, [pendingCustomerId, supplierCustomers]);

  // Load the selected supplier's rate card (default + postcode-prefix rates).
  useEffect(() => {
    if (!selectedSupplierId) { setRateCard({ default_rate: null, rates: [] }); return; }
    fetch(`${API_URL}/api/contacts/haulage-suppliers/${selectedSupplierId}/rates`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => setRateCard({ default_rate: data.default_rate ?? null, rates: data.rates || [] }))
      .catch(err => console.error('Failed to load supplier rates:', err));
  }, [selectedSupplierId]);

  const selectedSupplier = suppliers.find(s => String(s.id) === String(selectedSupplierId));
  const selectedCustomer = supplierCustomers.find(c => String(c.id) === String(selectedCustomerId));

  /*
   * The supplier's own contact groups, which a contact created here is put into.
   *
   * NWF Agriculture is in "NWF Delivery Accounts", Pickards in "Pickards
   * Delivery Accounts", and the customer dropdown already lists anyone sharing
   * a group with the supplier — so putting the new contact in the supplier's
   * group is what makes it appear in the list it was created for.
   *
   * Read off the supplier rather than matched on its name: the endpoint already
   * returns every group bar "Haulage Job Suppliers" itself, so a third supplier
   * added next year works with no code change and no name to keep in step.
   */
  const supplierGroups = Array.isArray(selectedSupplier?.groups) ? selectedSupplier.groups : [];

  // The delivery postcode drives the auto-selected rate.
  const deliveryPostcode = selectedCustomer?.postcode || '';
  const normPostcode = (deliveryPostcode || '').toUpperCase().replace(/\s+/g, '');
  // Longest postcode-prefix the delivery postcode starts with (most specific wins).
  const matchedRate = normPostcode
    ? [...rateCard.rates]
        .filter(r => normPostcode.startsWith((r.postcode_prefix || '').toUpperCase()))
        .sort((a, b) => (b.postcode_prefix || '').length - (a.postcode_prefix || '').length)[0]
    : null;
  // Flag: a postcode is present but no prefix matches — the user should add it.
  const rateFlag = !!normPostcode && !matchedRate;

  // Re-suggest the rate whenever the postcode or rate card changes. The user can
  // still override afterwards (this only fires on those inputs changing).
  useEffect(() => {
    // The job's OWN rate band wins over the one the postcode would suggest:
    // it is what was agreed and what has been charged, and re-deriving it on
    // open would silently re-price a job somebody only came here to re-date.
    if (pendingRate) {
      const fromCard = pendingRate.key === 'default'
        ? (rateCard.default_rate != null ? parseFloat(rateCard.default_rate) : null)
        : (rateCard.rates.find(r => r.postcode_prefix === pendingRate.key)?.rate ?? null);
      const overridden = pendingRate.rate != null
        && (fromCard == null || Math.abs(Number(fromCard) - pendingRate.rate) > 0.005);
      if (overridden) {
        setSelectedRateKey(MANUAL_RATE);
        setManualRate(String(pendingRate.rate));
      } else {
        setSelectedRateKey(pendingRate.key);
      }
      setPendingRate(null);
      return;
    }
    setSelectedRateKey(matchedRate ? matchedRate.postcode_prefix : 'default');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normPostcode, rateCard]);

  const rateOptions = [
    { value: 'default', label: rateCard.default_rate != null ? `Default — £${Number(rateCard.default_rate).toFixed(2)}` : 'Default — (not set)' },
    ...rateCard.rates.map(r => ({ value: r.postcode_prefix, label: `${r.postcode_prefix} — £${Number(r.rate).toFixed(2)}` })),
    { value: MANUAL_RATE, label: 'Other — enter a rate' }
  ];

  /** What the CARD says for a band, ignoring any override. */
  const cardRateFor = (key) => (key === 'default'
    ? (rateCard.default_rate != null ? parseFloat(rateCard.default_rate) : null)
    : (rateCard.rates.find(r => r.postcode_prefix === key)?.rate ?? null));

  const resolvedRate = selectedRateKey === MANUAL_RATE
    ? (manualRate === '' || Number.isNaN(parseFloat(manualRate)) ? null : parseFloat(manualRate))
    : cardRateFor(selectedRateKey);

  // badge: undefined (not ' ') for account-less contacts so SearchableDropdown
  // omits the badge entirely rather than reserving an invisible placeholder.
  const supplierOptions = suppliers.map(s => withStoreTag(s, {
    value: String(s.id),
    badge: s.account_no || undefined,
    label: s.company_name || '',
    searchText: `${s.account_no || ''} ${s.company_name || ''}`
  }));

  const customerOptions = supplierCustomers.map(c => ({
    value: String(c.id),
    badge: c.account_no || undefined,
    label: c.company_name + (c.address_line1 ? ` (${c.address_line1})` : ''),
    searchText: `${c.account_no || ''} ${c.company_name || ''} ${c.address_line1 || ''}`
  }));

  const loadSupplierCustomers = async () => {
    const res = await fetch(`${API_URL}/api/contacts/haulage-suppliers/${selectedSupplierId}/customers`,
      { credentials: 'include' });
    const list = await res.json();
    setSupplierCustomers(list);
    return list;
  };

  /*
   * A contact made here is assigned to this supplier before it is selected.
   *
   * The dropdown lists only the supplier's assigned customers, so without the
   * link the contact would be created and then be unselectable — the button
   * would appear to do nothing. Assigning is also what was meant: it is being
   * created in order to deliver to them for this supplier.
   */
  const handleContactCreated = async (_createdInXero, created) => {
    if (!created?.id) return;
    try {
      await fetch(`${API_URL}/api/contacts/haulage-suppliers/${selectedSupplierId}/customers`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: created.id })
      });
      const list = await loadSupplierCustomers();
      if (list.some(c => String(c.id) === String(created.id))) {
        setSelectedCustomerId(String(created.id));
      }
    } catch (error) {
      setAlertDialog({
        isOpen: true,
        type: 'error',
        message: `The contact was created, but assigning it to this supplier failed: ${error.message}. Add it under Contact Groups → Haulage Job Suppliers.`
      });
    }
  };

  const resetForm = () => {
    setSelectedSupplierId('');
    setSelectedCustomerId('');
    setDeliveryDate('');
    setProductDetails('');
    setQuantity('');
    setUnits('');
    setDeliveryInstructions('');
    setMoffett(false);
    setMoffettCharge('5.00');
    setManualRate('');
  };

  /* Units is required. A quantity with no unit is ambiguous on the paperwork
     a driver loads from — 40 is forty bags or forty tonnes — which is the same
     reason the PO documents carry a unit suffix. It opens on "Select units"
     rather than a guess, because a wrong unit is worse than none. */
  const canSave = selectedSupplierId && selectedCustomerId && units && !saving
    // A rate picker set to "Other" with nothing typed would save a null rate
    // and read back as the card's default.
    && !(selectedRateKey === MANUAL_RATE && resolvedRate == null);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const body = {
        customer_account_id: parseInt(selectedCustomerId, 10),
        delivery_date: deliveryDate || null,
        product_details: productDetails || null,
        quantity: quantity === '' ? null : parseFloat(quantity),
        units: units || null,
        delivery_instructions: deliveryInstructions || null,
        rate: resolvedRate,
        // A typed rate belongs to no band, so it carries no prefix — the same
        // shape as a default one, which is why reading it back compares money.
        rate_postcode_prefix: (selectedRateKey === 'default' || selectedRateKey === MANUAL_RATE)
          ? null : selectedRateKey,
        moffett_charge: moffett && moffettCharge !== '' ? parseFloat(moffettCharge) : null
      };
      // The supplier is fixed once a job exists — the job number is issued
      // against them and the rate card is theirs — so it is sent on create
      // only. Delivery status is the mirror image: a job being raised has not
      // been delivered.
      if (editMode) body.delivery_status = deliveryStatus;
      else body.supplier_account_id = parseInt(selectedSupplierId, 10);

      const response = await fetch(
        editMode ? `${API_URL}/api/haulage-jobs/${editingJobId}` : `${API_URL}/api/haulage-jobs`,
        {
          method: editMode ? 'PATCH' : 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Request failed');
      if (onNavigate) onNavigate('haulage-jobs');
    } catch (error) {
      setAlertDialog({
        isOpen: true,
        message: `Failed to ${editMode ? 'save' : 'create'} haulage job: ${error.message}`,
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => { if (onNavigate) onNavigate('haulage-jobs'); };

  return (
    <div className="customer-order-container create-haulage-job">
      <div className="search-header">
        <div className="workflow-info">
          <span className="info">{editMode ? `Edit ${editJobNumber}` : 'Create a haulage job'}</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span className="note-text">
              {editMode
                ? 'The supplier is fixed once a job exists — the job number and rate card are theirs'
                : 'Haul product for a supplier — no purchase order is created'}
            </span>
          </span>
        </div>
        <div className="search-header-inner">
          <div className="buttons slideout">
            <div>
              <button data-read-safe className="btn-round btn-danger" onClick={handleCancel} disabled={saving}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                <span className="btn-label">Cancel</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Loading an existing job: the form is built from it, so showing the
          empty create form first and letting it fill in would read as fields
          changing under the user. */}
      {loadingJob && <LoadingSpinner />}

      {/* Supplier, who it goes to, and when — one row. The date belongs here
          rather than among the product details: it is part of choosing the job,
          and it is what the rate and the planning screen key off. */}
      <div className="section job-top-row">
        <div className="form-group">
          <label className="section-title tight">Select Supplier</label>
          {/* Locked on an existing job: the job number is issued against the
              supplier and the rate card is theirs, so changing it would leave
              a job priced off somebody else's card. Shown rather than hidden,
              because who is hauling is part of reading the job. */}
          <SearchableDropdown
            value={selectedSupplierId}
            onChange={setSelectedSupplierId}
            options={[{ value: '', label: 'Select Supplier' }, ...supplierOptions]}
            placeholder="Select Supplier"
            searchPlaceholder="Search by # or name..."
            disabled={editMode}
            title={editMode ? 'The supplier cannot be changed once a job exists — raise a new job instead' : undefined}
            {...MENU_ESCAPE}
          />
        </div>

        <div className="form-group">
          <div className="customer-label-row">
            <label className="section-title tight">Delivery Customer</label>
            {/* Replaces the manual-address form. Same keystrokes, but it leaves
                a contact record behind — with notes and vehicle access that
                show up beneath, and a name that is simply in the list next
                time. Disabled until a supplier is chosen, because the new
                contact is assigned to that supplier on the way in. */}
            <button
              type="button"
              className="btn-full hj-new-contact"
              onClick={() => setShowNewContact(true)}
              disabled={!selectedSupplierId || saving}
              title={selectedSupplierId ? 'Create a contact and assign it to this supplier' : 'Choose a supplier first'}
              data-write-action
            >
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">add</span>
              </div>
              <span className="btn-label">New Contact</span>
            </button>
          </div>
          <div className="customer-picker-row">
            <SearchableDropdown
              value={selectedCustomerId}
              onChange={setSelectedCustomerId}
              options={[{ value: '', label: 'Select Customer' }, ...customerOptions]}
              placeholder="Select Customer"
              searchPlaceholder="Search by # or name..."
              {...MENU_ESCAPE}
            />
            {/* The panel of address and phone that used to sit to the right is
                a contact record shown badly. This opens the real one. */}
            <button
              type="button"
              className="btn-round"
              onClick={() => setShowContactModal(true)}
              disabled={!selectedCustomerId}
              title={selectedCustomerId ? 'View this contact' : 'Select a customer first'}
            >
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">visibility</span>
              </div>
            </button>
          </div>
        </div>

        <div className="form-group">
          <label className="section-title tight">Delivery Date</label>
          <DateInput value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)} />
        </div>
      </div>

      {/* The job, as one line on the same grid the order, invoice, purchase
          order and bill screens use — so a haulage job reads like every other
          document in the system. No totals panel and no "add line": a job is
          one delivery, and the money is the haulage rate, not a line sum. */}
      <div className="section job-lines-section">
        <label className="section-title">Job Details</label>
        <div className="job-lines">
          <div className="order-lines-grid job-lines-grid">
            <table className="line-items-table order-grid">
              <thead>
                <tr>
                  <th className="col-product">Product Details</th>
                  <th className="col-num">Qty</th>
                  <th className="col-units">Units <span className="required-mark" title="Required">*</span></th>
                  <th className="col-rate">Haulage Rate{deliveryPostcode ? ` (${deliveryPostcode})` : ''}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <Input value={productDetails} onChange={e => setProductDetails(e.target.value)}
                      placeholder="e.g. 16% Layers Pellets" />
                  </td>
                  <td className="col-num">
                    <Input type="number" step="1" min="0" value={quantity}
                      onChange={e => setQuantity(e.target.value)} placeholder="0" />
                  </td>
                  <td>
                    <SearchableDropdown
                      value={units}
                      onChange={setUnits}
                      options={[{ value: '', label: 'Select units' }, ...UNIT_OPTIONS]}
                      placeholder="Select units"
                      searchPlaceholder="Search units..."
                      className={units ? undefined : 'field-required-empty'}
                      {...MENU_ESCAPE}
                    />
                  </td>
                  <td>
                    {/* Not a SearchableDropdown, but it sits in the same
                        clipped cell and takes the same two props, so it opens
                        into the same real space rather than clamping beside a
                        neighbour that doesn't. */}
                    <div className="hj-rate-cell">
                      <Dropdown value={selectedRateKey} onChange={setSelectedRateKey}
                        options={rateOptions} {...MENU_ESCAPE} />
                      {selectedRateKey === MANUAL_RATE && (
                        <Input
                          type="number" step="0.01" min="0"
                          value={manualRate}
                          onChange={(e) => setManualRate(e.target.value)}
                          placeholder="0.00"
                          aria-label="Haulage rate"
                        />
                      )}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Only on an existing job: one being raised has not been
              delivered, and offering the status at creation would invite
              somebody to say it had. */}
          {editMode && (
            <div className="form-group hj-delivery-status">
              <label className="section-title tight">Delivery Status</label>
              <Dropdown
                value={deliveryStatus}
                onChange={setDeliveryStatus}
                options={DELIVERY_STATUSES.map(v => ({ value: v, label: v }))}
                {...MENU_ESCAPE}
              />
            </div>
          )}

          {/* Outside the table, because it is not part of the delivery — it is
              a charge added to the job. The head mirrors the grid's own so the
              toggle lands on the body row's centre whatever height it takes. */}
          <div className="job-moffett">
            <div className="job-moffett-head">Moffett</div>
            <div className="job-moffett-body">
              <div className="checkbox-container">
                <div className="check light">
                  <input id="ho-moffett" type="checkbox" checked={moffett}
                    onChange={e => setMoffett(e.target.checked)} />
                  <label htmlFor="ho-moffett"></label>
                </div>
              </div>
              {moffett && (
                <Input type="number" step="0.01" min="0" value={moffettCharge}
                  onChange={e => setMoffettCharge(e.target.value)} placeholder="5.00" />
              )}
            </div>
          </div>
        </div>

        {rateFlag && (
          <div className="rate-flag">
            <span className="material-symbols-rounded">warning</span>
            <span>No rate set for postcode <strong>{normPostcode.slice(0, 2)}</strong> — defaulting. Add the postcode &amp; rate in Contact Groups → Haulage Job Suppliers.</span>
          </div>
        )}

        {/* Delivery instructions beside what we already know about this
            customer — the same pair as the foot of Create Customer Order, and
            for the same reason: the notes and the vehicle access are most
            likely to change what someone types next to them. */}
        <div className="job-footer-grid">
          <div className="footer-col">
            <label>Delivery Instructions</label>
            <Textarea value={deliveryInstructions} onChange={e => setDeliveryInstructions(e.target.value)} rows={5}
              placeholder="Access, timing, contact on arrival, etc." />
          </div>
          <div className="footer-col">
            <CustomerNotesPanel
              notes={selectedCustomer?.notes}
              largestVehicleAccess={selectedCustomer?.largest_vehicle_access}
            />
          </div>
        </div>
      </div>

      {/* Under everything, as on Create Customer Order. */}
      <div className="form-actions">
        <button
          className="btn-slideout"
          onClick={() => setConfirmDialog({
            isOpen: true,
            title: 'Clear this job?',
            message: 'Clear the supplier, customer, date, product details and notes?',
            confirmText: 'Clear',
            onConfirm: () => {
              setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
              resetForm();
            }
          })}
          disabled={saving}
        >
          <div className="icon-wrapper">
            <span className="material-symbols-rounded">replay</span>
          </div>
          <span className="btn-label">Clear Job</span>
        </button>
        <button className="btn-slideout" onClick={handleSave} disabled={!canSave}>
          <div className="icon-wrapper">
            <span className={`material-symbols-rounded${saving ? ' spinning' : ''}`}>{saving ? 'progress_activity' : 'save'}</span>
          </div>
          <span className="btn-label">{saving ? 'Saving…' : (editMode ? 'Save Changes' : 'Save Job')}</span>
        </button>
      </div>

      {showNewContact && (
        <ContactEditModal
          contact={{ contact_type: 'CUSTOMER', groupsArray: supplierGroups }}
          onClose={() => setShowNewContact(false)}
          onSave={handleContactCreated}
        />
      )}

      {showContactModal && selectedCustomerId && (
        <ContactModal
          contactId={selectedCustomerId}
          onClose={() => setShowContactModal(false)}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ isOpen: false, message: '', onConfirm: null })}
      />

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
      />
    </div>
  );
}

export default CreateHaulageJob;
